import React from 'react'
import axios from 'axios'
import moment from 'moment'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import toastr from '@/lib/notifications'
import DatePickerInput from '../../date_picker_input'
import store from '../../../state/store'

class AddClassroomOrder extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      saving: false,
      data: this.props.data,
      grade: this.props.data.selected_grade || '',
      classroom_ids: [],
      months_to_bill: this.defaultMonthsToBill(this.props.data),
      enable_trial: false,
      first_billing_at: '',
      ...(this.promoFreeMonth() ? this.trialState() : {}),
    }

    this.onGradeChange = this.onGradeChange.bind(this)
    this.onMonthsToBillChange = this.onMonthsToBillChange.bind(this)
    this.onEnableTrialChange = this.onEnableTrialChange.bind(this)
    this.onFirstBillingAtChange = this.onFirstBillingAtChange.bind(this)
    this.onSave = this.onSave.bind(this)
  }

  isBootcamps() {
    return this.props.plan === 'bootcamps'
  }

  // The promo's free month is the order's trial (never on top of one), so the trial is locked on
  promoFreeMonth() {
    const promo = this.props.data.promo
    return !this.isBootcamps() && !!promo && promo.free_months > 0
  }

  trialState() {
    return {
      enable_trial: true,
      months_to_bill: 1,
      first_billing_at: moment().add(1, 'month').format('YYYY-MM-DD'),
    }
  }

  defaultMonthsToBill(data) {
    const options = data.pricing_options || []
    if (options.length === 0) {
      return 1
    }
    const monthly = options.find((option) => option.months_to_bill === 1)
    return monthly ? monthly.months_to_bill : options[0].months_to_bill
  }

  onGradeChange(grade) {
    axios.get(`/api/sales/enquiries/${this.props.enquiryId}/classroom_orders/new`, {
      params: { enquiry_learner_id: this.props.enquiryLearnerId, plan: 'classrooms', grade: grade },
    })
      .then((response) => {
        this.setState({
          data: response.data,
          grade: grade,
          classroom_ids: [],
          months_to_bill: this.defaultMonthsToBill(response.data),
        })
      })
      .catch((error) => {
        const message = (error && error.response && error.response.data && error.response.data.custom_error_message) || 'Failed to load classes.'
        toastr.error(message)
      })
  }

  onToggleClassroom(id) {
    if (this.state.classroom_ids.includes(id)) {
      this.setState({ classroom_ids: this.state.classroom_ids.filter((classroomId) => classroomId !== id) })
    } else {
      this.setState({ classroom_ids: [...this.state.classroom_ids, id] })
    }
  }

  onMonthsToBillChange(value) {
    this.setState({ months_to_bill: parseInt(value, 10) })
  }

  onEnableTrialChange(checked) {
    if (checked === true) {
      this.setState(this.trialState())
    } else {
      this.setState({
        enable_trial: false,
        months_to_bill: this.defaultMonthsToBill(this.state.data),
        first_billing_at: '',
      })
    }
  }

  onFirstBillingAtChange(value) {
    this.setState({ first_billing_at: value })
  }

  trialEndLabel() {
    if (!this.state.first_billing_at) {
      return null
    }

    return moment(this.state.first_billing_at).subtract(1, 'day').format('Do MMM YYYY')
  }

  itemAmounts() {
    if (this.isBootcamps()) {
      return this.state.data.item_amounts || []
    }

    const options = this.state.data.pricing_options || []
    const selected = options.find((option) => option.months_to_bill === this.state.months_to_bill)
    return selected ? selected.item_amounts : []
  }

  selectedTotal() {
    return this.itemAmounts()
      .slice(0, this.state.classroom_ids.length)
      .reduce((total, amount) => total + amount, 0)
  }

  onSave() {
    const params = {
      enquiry_learner_id: this.props.enquiryLearnerId,
      plan: this.props.plan,
      classroom_ids: this.state.classroom_ids,
      learner_grade: this.state.grade,
      months_to_bill: this.state.months_to_bill,
      trial_months: this.state.enable_trial ? 1 : 0,
      first_billing_at: this.state.enable_trial ? this.state.first_billing_at : null,
    }

    this.setState({ saving: true })

    axios.post(`/api/sales/enquiries/${this.props.enquiryId}/classroom_orders`, params)
      .then(() => {
        if (this.state.enable_trial) {
          toastr.success('Trial activated.')
        } else {
          toastr.success('Order created. The customer can pay for it from their dashboard.')
        }
        store.emit('orders:index', this.props.enquiryId)
        store.emit('customer_orders:refresh')
        this.props.onDone()
      })
      .catch((error) => {
        this.setState({ saving: false })
        const message = (error && error.response && error.response.data && error.response.data.custom_error_message) || 'Failed to create the order.'
        toastr.error(message)
      })
  }

  render() {
    const data = this.state.data
    const classrooms = data.available_classrooms || []
    const itemAmounts = this.itemAmounts()
    const total = this.selectedTotal()

    return (
      <div role="form">
        <div className="mb-4 rounded-lg border bg-muted px-4 py-3 text-sm text-muted-foreground">
          {this.isBootcamps() ? 'Bootcamp' : 'Classroom'} orders are standalone — they are not part of this enquiry's sales orders. The customer pays for them from their dashboard.
        </div>

        {data.promo && (
          <div className="mb-4 rounded-[10px] border border-info/30 bg-info/10 px-4 py-3 text-sm">
            Prices include {data.promo.percentage}% off with the {data.promo.name} promotion.
            {this.promoFreeMonth() && ' The first month is free, so this order starts as a free trial and bills monthly after that.'}
          </div>
        )}

        {!this.isBootcamps() && (
          <div className="mb-4 flex flex-wrap items-center">
            <Label className="w-full md:w-1/3 px-3">Grade</Label>
            <div className="w-full md:w-2/3 px-3">
              <Select value={this.state.grade} onValueChange={this.onGradeChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Select grade" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {(data.available_grades || []).map((grade) => (
                      <SelectItem key={grade} value={grade}>{grade}</SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        <div className="mb-4 flex flex-wrap">
          <Label className="w-full md:w-1/3 px-3 pt-2">Classes</Label>
          <div className="w-full md:w-2/3 px-3 flex flex-col gap-2">
            {classrooms.length === 0 && (
              <p className="text-sm text-muted-foreground pt-2">
                {this.isBootcamps() ? 'No upcoming bootcamps available.' : 'No classes available. Select a grade first.'}
              </p>
            )}
            {classrooms.map((classroom) => {
              const selected = this.state.classroom_ids.includes(classroom.id)
              const position = this.state.classroom_ids.indexOf(classroom.id)
              const amount = selected ? itemAmounts[position] : itemAmounts[this.state.classroom_ids.length]

              return (
                <label key={classroom.id} className="flex items-start gap-2 rounded-md border p-3 text-sm cursor-pointer">
                  <Checkbox checked={selected} onCheckedChange={() => this.onToggleClassroom(classroom.id)} />
                  <span className="flex-1">
                    <span className="font-medium">
                      {classroom.tier === 'advanced' ? `Advanced ${classroom.subject}` : classroom.subject}
                      {this.isBootcamps() && ` (${classroom.grade}, ${(classroom.curriculum || '').toUpperCase()})`}
                      {!this.isBootcamps() && classroom.curriculum === 'ieb' && ' (IEB)'}
                    </span>
                    {classroom.teacher_name && <span> with {classroom.teacher_name}</span>}
                    <span className="block text-muted-foreground">{classroom.schedule}</span>
                    {this.isBootcamps() && classroom.start_date && (
                      <span className="block text-muted-foreground">Starts {classroom.start_date}</span>
                    )}
                  </span>
                  {amount != null && <span className="font-semibold">R {amount}</span>}
                </label>
              )
            })}
          </div>
        </div>

        {!this.isBootcamps() && (
          <div className="mb-4 flex flex-wrap items-center">
            <Label className="w-full md:w-1/3 px-3">Billing period</Label>
            <div className="w-full md:w-2/3 px-3">
              <Select
                value={String(this.state.months_to_bill)}
                onValueChange={this.onMonthsToBillChange}
                disabled={this.state.enable_trial}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select billing period" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {(data.pricing_options || []).map((option) => (
                      <SelectItem key={option.months_to_bill} value={String(option.months_to_bill)}>
                        {option.months_to_bill === 12 ? 'Until end of year' : `${option.months_to_bill} month${option.months_to_bill > 1 ? 's' : ''}`}
                        {option.discount_percentage > 0 && ` (${option.discount_percentage}% off)`}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {!this.isBootcamps() && (
          <div className="mb-4 flex flex-wrap items-center">
            <Label className="w-full md:w-1/3 px-3">Free trial</Label>
            <div className="w-full md:w-2/3 px-3">
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={this.state.enable_trial}
                  onCheckedChange={this.onEnableTrialChange}
                  disabled={this.promoFreeMonth()}
                />
                {this.promoFreeMonth()
                  ? `First month free with the ${data.promo.name} promotion (activates immediately, no payment)`
                  : 'Free trial (activates immediately, no payment)'}
              </label>
            </div>
          </div>
        )}

        {!this.isBootcamps() && this.state.enable_trial && (
          <div className="mb-4 flex flex-wrap items-center">
            <Label className="w-full md:w-1/3 px-3">First billing date</Label>
            <div className="w-full md:w-2/3 px-3">
              <DatePickerInput
                value={this.state.first_billing_at || ''}
                onChange={this.onFirstBillingAtChange}
              />
              {this.trialEndLabel() && (
                <p className="mt-1 text-xs text-muted-foreground">
                  Free trial until {this.trialEndLabel()}, then a monthly subscription billed from this date. Must be within 45 days.
                </p>
              )}
            </div>
          </div>
        )}

        <div className="mb-4 flex flex-wrap items-center">
          <Label className="w-full md:w-1/3 px-3">{this.isBootcamps() ? 'Total (once-off)' : 'Total'}</Label>
          <div className="w-full md:w-2/3 px-3 text-lg font-semibold">
            R {total}
          </div>
        </div>

        <Button
          className="mt-6 w-full"
          onClick={this.onSave}
          disabled={this.state.saving || this.state.classroom_ids.length === 0}
        >
          {this.state.enable_trial ? 'Activate Trial' : 'Create Order'}
        </Button>
      </div>
    )
  }
}

export default AddClassroomOrder
